'use client';

import { useContext, useMemo, useState, type FC } from 'react';
import {
  DeleteQuestionnairesDialog,
  Dropdown,
  EditDueDateDialog,
  Button,
  ButtonGroup,
  ResultListItem,
  EditTemplateReminderDialog,
  RestoreQuestionnaireDialog,
} from '@/_components';
import {
  generateQuestionnaireLink,
  saveQuestionnaire,
} from '@/_api/client-questionnaires';
import {
  lockQuestionnaires,
  saveQuestionnaires,
  sendReminders,
} from '@/_api/admin-questionnaires';
import { useCopy } from '@/_hooks/useCopy';
import { QuestionnaireContext } from '@/_contexts/admin/QuestionnaireProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { OverviewContext } from '@/_contexts/admin/OverviewProvider';
import type { ActionsCellProps } from './ActionsCell.types';
import { UnattachedPreviewDialog } from '@/_components/QuestionnairePreview/components/UnattachedPreviewDialog';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import {
  getAdminDocumentDropOffPreviewLink,
  getAdminQuestionnaireViewLink,
} from '@/_utils/admin';
import { getHasPermission } from '@/_utils/permissions';
import { PermissionsMapEnum } from '@/_types/permissions';

export const ActionsCell: FC<ActionsCellProps> = ({
  questionnaire,
  setSelectedQuestionnaires,
}) => {
  const { addToast } = useContext(ToastContext);
  const { refreshCompanyQuestionnaires, selectedCompany } =
    useContext(CompanyContext);
  const {
    loadQuestionnaires: loadQuestionnairesFromContext,
    setQuestionnaireExport,
    onExportToggle,
    setQuestionnairePreview,
    reloadQuestionnairePreview,
  } = useContext(QuestionnaireContext);
  const { permissions, token } = useContext(UserContext);
  const { filter } = useContext(OverviewContext);
  const { getDocumentsUploadedOutside } = useContext(PreviewContext);
  const { copyTextToClipboard } = useCopy();

  const isReminderDisabled: boolean = questionnaire.reminders_disabled;

  const [isRestoreDialogOpened, setIsRestoreDialogOpened] = useState(false);
  const [
    isEditTemplateReminderDialogOpened,
    setIsEditTemplateReminderDialogOpened,
  ] = useState(false);
  const [isEditDueDateDialogOpened, setIsEditDueDateDialogOpened] =
    useState(false);
  const [
    isDeleteQuestionnairesDialogOpened,
    setIsDeleteQuestionnairesDialogOpened,
  ] = useState(false);
  const [showUnattachedPreviewDialog, setShowUnattachedPreviewDialog] =
    useState(false);
  const [unattachedDocuments, setUnattachedDocuments] = useState<any[]>([]);

  const isSendRemindersEnabled: boolean = useMemo(() => {
    return (
      !questionnaire.locked &&
      !questionnaire.completed &&
      !questionnaire.is_draft
    );
  }, [questionnaire.locked, questionnaire.completed, questionnaire.is_draft]);

  const loadQuestionnaires = async () => {
    if (selectedCompany) {
      await refreshCompanyQuestionnaires();
    } else {
      await loadQuestionnairesFromContext();
    }
  };

  const onCopyClientViewLink = async () => {
    try {
      const { link } = (await generateQuestionnaireLink({
        companyId: questionnaire.company_id!,
        questionnaireId: questionnaire.id!,
        token,
      })) as any;

      await copyTextToClipboard(link);
      addToast('success', 'Link copied to clipboard');
    } catch (error) {
      addToast('error', null, { error });
    }
  };

  const onCopyAdvisorViewLink = async () => {
    if (!questionnaire.company_id || !questionnaire.id) return;

    try {
      if (questionnaire.document_drop_off_enabled) {
        // For document-drop-off, generate a real token-based URL that works without authentication
        const { link } = (await generateQuestionnaireLink({
          companyId: questionnaire.company_id,
          questionnaireId: questionnaire.id,
          token,
        })) as any;

        if (link) {
          // Extract token from the generated link and create preview URL
          const url = new URL(link, window.location.origin);
          const tokenParam = url.searchParams.get('token');

          if (tokenParam) {
            const previewLink = getAdminDocumentDropOffPreviewLink(
              undefined,
              undefined,
              tokenParam,
            );
            if (previewLink) {
              await copyTextToClipboard(previewLink);
              addToast('success', 'Preview link copied to clipboard');
            }
          }
        } else {
          throw new Error('Failed to generate questionnaire link');
        }
      } else {
        // For regular questionnaires, use the standard admin view link
        const previewLink: string = getAdminQuestionnaireViewLink(
          questionnaire.id,
          questionnaire.company_id,
          true,
        );
        await copyTextToClipboard(previewLink);
        addToast('success', 'Link copied to clipboard');
      }
    } catch (error) {
      addToast('error', null, { error });
    }
  };

  const onEditReminderStatus = async () => {
    try {
      await saveQuestionnaires({
        reminders_disabled: !questionnaire.reminders_disabled,
        questionnaire_ids: [questionnaire.id],
        token,
      });

      await loadQuestionnaires();
      addToast('success', 'Notifications successfully updated');
    } catch (error) {
      addToast('error', null, { error });
    }
  };

  const onLockQuestionnaires = async () => {
    try {
      await lockQuestionnaires({
        questionnaireIds: [questionnaire.id],
        token,
      });

      await loadQuestionnaires();

      addToast('success', 'Questionnaire successfully locked');
    } catch (error) {
      addToast('error', null, { error });
    }
  };

  const onUnlockQuestionnaires = async () => {
    try {
      await saveQuestionnaire({
        companyId: questionnaire.company_id!,
        id: questionnaire.id!,
        params: {
          locked: false,
        },
        token,
      });

      await loadQuestionnaires();

      addToast('success', 'Questionnaire successfully unlocked');
    } catch (error) {
      addToast('error', null, { error });
    }
  };

  const handleExportClick = async () => {
    try {
      const response = await getDocumentsUploadedOutside(
        questionnaire.company_id!,
        true,
      );

      if (!response.error) {
        const documents = response as unknown as any[];
        setUnattachedDocuments(documents ?? []);

        if (documents?.length > 0) {
          setQuestionnairePreview(questionnaire);
          setShowUnattachedPreviewDialog(true);
        } else {
          onExportQuestionnaire();
        }
      } else {
        onExportQuestionnaire();
      }
    } catch (error) {
      // If there's an error getting documents, proceed with export
      onExportQuestionnaire();
      console.error('error pulling unattached documents', error);
    }
  };

  const onExportQuestionnaire = () => {
    setShowUnattachedPreviewDialog(false);
    onExportToggle();
    setQuestionnaireExport([questionnaire]);
  };

  const onSendReminder = async () => {
    try {
      const baseParams = {
        questionnaire_ids: [questionnaire.id],
      };

      const response = await sendReminders({
        params: baseParams,
        token,
      });

      if (response.error) {
        addToast('error', response.error);
        return;
      }

      addToast('success', 'Reminders sent successfully');
    } catch (error) {
      console.error(error);
      addToast('error', null, { error });
    }
  };

  const hasSendQuestionnairePermission: boolean = getHasPermission(
    PermissionsMapEnum['questionnaire.add'],
    permissions,
  );

  const hasEditQuestionnairePermission: boolean = getHasPermission(
    PermissionsMapEnum['questionnaire.edit'],
    permissions,
    questionnaire?.company_id,
  );

  const hasDeleteQuestionnairePermission: boolean = getHasPermission(
    PermissionsMapEnum['questionnaire.delete'],
    permissions,
    questionnaire?.company_id,
  );

  const items: Array<ResultListItem> = filter?.show_only_deleted
    ? [
        {
          name: 'Restore Questionnaire',
          onClick: () => setIsRestoreDialogOpened(true),
          isDisabled: !hasSendQuestionnairePermission,
        },
      ]
    : [
        {
          name: 'Export Questionnaire',
          onClick: handleExportClick,
        },
        ...(!questionnaire.locked
          ? [
              {
                name: 'Lock Questionnaire',
                onClick: onLockQuestionnaires,
                isDisabled: !hasEditQuestionnairePermission,
              },
            ]
          : [
              {
                name: 'Unlock Questionnaire',
                onClick: onUnlockQuestionnaires,
                isDisabled: !hasEditQuestionnairePermission,
              },
            ]),
        {
          name: 'Delete Questionnaire',
          onClick: () => setIsDeleteQuestionnairesDialogOpened(true),
          isDisabled: !hasDeleteQuestionnairePermission,
        },
        {
          name: 'Copy Questionnaire Link',
          items: [
            {
              name: 'Client View Link',
              onClick: onCopyClientViewLink,
            },
            {
              name: 'Admin View Link',
              onClick: onCopyAdvisorViewLink,
            },
          ],
        },
        { type: 'separator' },
        {
          name: 'Edit Due Date',
          onClick: () => setIsEditDueDateDialogOpened(true),
          isDisabled: !hasEditQuestionnairePermission,
        },
        ...(isSendRemindersEnabled
          ? [
              {
                name: 'Reminders',
                label: isReminderDisabled ? 'Disabled' : '',
                items: [
                  ...(!isReminderDisabled
                    ? [
                        {
                          name: 'Send Reminder',
                          onClick: onSendReminder,
                          isDisabled: !hasSendQuestionnairePermission,
                        },
                        {
                          name: 'Edit Message',
                          onClick: () =>
                            setIsEditTemplateReminderDialogOpened(true),
                          isDisabled: !hasEditQuestionnairePermission,
                        },
                      ]
                    : []),
                  {
                    name: isReminderDisabled
                      ? 'Enable Reminders'
                      : 'Disable Reminders',
                    onClick: () => onEditReminderStatus(),
                    isDisabled: !hasEditQuestionnairePermission,
                  },
                ],
              },
            ]
          : []),
      ];

  return (
    <>
      <EditTemplateReminderDialog
        isOpen={isEditTemplateReminderDialogOpened}
        onClose={() => setIsEditTemplateReminderDialogOpened(false)}
        selectedQuestionnaires={[questionnaire]}
        title="Edit Reminder"
      />
      <EditDueDateDialog
        isOpen={isEditDueDateDialogOpened}
        loadQuestionnaires={loadQuestionnaires}
        onClose={() => setIsEditDueDateDialogOpened(false)}
        selectedCompanyId={questionnaire?.company_id}
        selectedQuestionnaires={[questionnaire]}
      />
      <DeleteQuestionnairesDialog
        isOpen={isDeleteQuestionnairesDialogOpened}
        loadQuestionnaires={loadQuestionnaires}
        onClose={() => setIsDeleteQuestionnairesDialogOpened(false)}
        selectedQuestionnaires={[questionnaire]}
        setSelectedQuestionnaires={setSelectedQuestionnaires}
      />
      <RestoreQuestionnaireDialog
        isOpen={isRestoreDialogOpened}
        onClose={() => setIsRestoreDialogOpened(false)}
        selectedQuestionnaires={[questionnaire]}
      />
      <UnattachedPreviewDialog
        isOpen={showUnattachedPreviewDialog}
        onClose={() => setShowUnattachedPreviewDialog(false)}
        initialDocuments={unattachedDocuments}
        onExportToggle={onExportQuestionnaire}
        questionnaire={questionnaire}
        reloadQuestionnairePreview={reloadQuestionnairePreview}
      />
      <ButtonGroup>
        <Dropdown items={items} placement="left">
          <Button
            iconLeft="dots-three"
            value="Actions"
            isIconOnly={true}
            size="small"
            intent="ghost"
          />
        </Dropdown>
      </ButtonGroup>
    </>
  );
};
